import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { AiOutlineDoubleRight } from 'react-icons/ai';
import { MdOutlineCheckBoxOutlineBlank, MdOutlineCheckBox, MdVisibility, MdInfo } from 'react-icons/md';
import Spinner from 'assets/gif/dots.gif';
import axios from 'config/axios';
import Header from 'components/Header';
import Popup from 'components/Popup';
import Table from 'components/Table';
import Tabs from 'components/Tabs';
import TextInput from 'components/TextInput';
import VocabWalkthrough from 'components/VocabWalkthrough';
import VocabQuizWalkthrough from 'components/VocabQuizWalkthrough';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';

import './index.css';

const VOCAB_TABS = ['Flashcard Sets', 'Vocab Quizzes'];

function Vocab() {
    const [mainTab, setMainTab] = useState(0);

    // Flashcard Sets state
    const [vocabSets, setVocabSets] = useState([]);
    const [selectedSetId, setSelectedSetId] = useState(null);
    const [students, setStudents] = useState([]);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [showWalkthrough, setShowWalkthrough] = useState(false);
    const [showQuizWalkthrough, setShowQuizWalkthrough] = useState(false);

    // Word preview popup state
    const [previewWords, setPreviewWords] = useState(null);
    const [previewSetName, setPreviewSetName] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const popupRef = useOutsideClickHandler(() => { setPreviewWords(null); setPreviewLoading(false); });

    // Quiz preview state
    const [quizPreview, setQuizPreview] = useState(null);
    const [quizPreviewLoading, setQuizPreviewLoading] = useState(false);
    const [quizPreviewTitle, setQuizPreviewTitle] = useState('');
    const quizPopupRef = useOutsideClickHandler(() => { setQuizPreview(null); setQuizPreviewLoading(false); });

    // Expanded set for quiz list
    const [expandedSetId, setExpandedSetId] = useState(null);

    const fetchVocabSets = () => {
        axios.get('vocab/tutor/sets/')
            .then(res => setVocabSets(res.data.vocabSets))
            .catch(error => console.error(error));
    };

    const fetchStudentsForSet = (vocabSetId: number) => {
        setStudentsLoading(true);
        setStudents([]);
        axios.get(`vocab/tutor/students/${vocabSetId}/`)
            .then(res => { setStudents(res.data.students); setStudentsLoading(false); })
            .catch(error => { console.error(error); setStudentsLoading(false); });
    };

    const fetchPreviewWords = (vocabSetId: number, setName: string) => {
        setPreviewLoading(true);
        setPreviewSetName(setName);
        setPreviewWords([]);
        axios.get(`vocab/tutor/sets/${vocabSetId}/words/`)
            .then(res => { setPreviewWords(res.data.words); setPreviewLoading(false); })
            .catch(error => { console.error(error); setPreviewWords(null); setPreviewLoading(false); });
    };

    const fetchQuizPreview = (vocabSetId: number, testNumber: number, subject: string) => {
        setQuizPreviewLoading(true);
        setQuizPreviewTitle(`${subject} — Quiz ${testNumber}`);
        setQuizPreview(null);
        axios.get(`vocab/tutor/sets/${vocabSetId}/quiz/${testNumber}/`)
            .then(res => { setQuizPreview(res.data); setQuizPreviewLoading(false); })
            .catch(error => { console.error(error); setQuizPreview(null); setQuizPreviewLoading(false); });
    };

    const updateStudentInPlace = (updatedStudent: any) => {
        setStudents(prev => prev.map(s =>
            s.studentId === updatedStudent.studentId ? updatedStudent : s
        ));
    };

    const toggleAssignment = (student: any) => {
        if (student.assigned) {
            axios.post('vocab/tutor/unassign/', { studentVocabSetId: student.assignmentId })
                .then(res => updateStudentInPlace(res.data.student))
                .catch(error => console.error(error));
        } else {
            axios.post('vocab/tutor/assign/', {
                vocabSetId: selectedSetId,
                studentId: student.studentId,
                organizationId: student.organizationId
            })
                .then(res => updateStudentInPlace(res.data.student))
                .catch(error => console.error(error));
        }
    };

    useEffect(() => {
        fetchVocabSets();
    }, []);

    useEffect(() => {
        if (selectedSetId) {
            fetchStudentsForSet(selectedSetId);
        }
    }, [selectedSetId]);

    return (
        <>
            <Helmet>
                <title>Vocab - Test Center Admin</title>
            </Helmet>
            <Header title='Vocab' />

            <div className='vocab__mainTabs'>
                <Tabs
                    tabs={VOCAB_TABS}
                    tabIndex={mainTab}
                    onSelect={VOCAB_TABS.map((_tab, t) => () => setMainTab(t))}
                />
            </div>

            {mainTab === 0 ? (
                /* ── Flashcard Sets tab ── */
                <div className='vocab'>
                    <div className='vocab__sets'>
                        <div className='vocab__bar1'>
                            <p className='vocab__text'>
                                Select a vocab set and assign students
                            </p>
                            <button className='vocab__howItWorks' onClick={() => setShowWalkthrough(true)}>
                                <MdInfo /> How it works
                            </button>
                        </div>
                        <Table
                            header={selectedSetId
                                ? ['Subject']
                                : ['Subject', '# Words', 'Preview', 'Assign']
                            }
                            rows={vocabSets.map(vs => (selectedSetId
                                ? [vs.subject]
                                : [
                                    vs.subject,
                                    vs.wordCount,
                                    <MdVisibility
                                        className='vocab__preview'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            fetchPreviewWords(vs.id, vs.subject);
                                        }}
                                    />,
                                    'Assign'
                                ]
                            ))}
                            columnTypes={selectedSetId
                                ? ['string']
                                : ['string', 'number', null, null]
                            }
                            rowsSelected={vocabSets.map(vs => (
                                vs.id === selectedSetId
                            ))}
                            onSelectRow={selectedSetId
                                ? vocabSets.map(vs => (
                                    () => setSelectedSetId(selectedSetId === vs.id ? null : vs.id)
                                ))
                                : undefined
                            }
                            onSelectCell={selectedSetId
                                ? undefined
                                : vocabSets.map(vs => ([
                                    null,
                                    null,
                                    () => fetchPreviewWords(vs.id, vs.subject),
                                    () => setSelectedSetId(vs.id)
                                ]))
                            }
                        />
                    </div>
                    {selectedSetId !== null && <>
                        <AiOutlineDoubleRight className='vocab__arrow' onClick={() => setSelectedSetId(null)} />
                        <div className='vocab__students'>
                            <div className='vocab__bar'>
                                <TextInput
                                    className='vocab__search'
                                    placeholder='search student'
                                    value={search}
                                    onChange={event => setSearch(event.target.value)}
                                />
                                <p className='vocab__text'>
                                    {students.filter(s => s.assigned).length} {students.filter(s => s.assigned).length === 1
                                        ? 'student'
                                        : 'students'
                                    } assigned
                                </p>
                            </div>
                            <Table
                                header={studentsLoading ? [''] : ['', 'Name', 'Email', 'Status']}
                                columnTypes={studentsLoading ? [null] : [null, 'string', 'string', 'string']}
                                rows={studentsLoading ? [[<img className='vocab__loader' src={Spinner} alt='Loading students...' />]] : students.filter(student => (
                                    !search ||
                                    student.email?.toLowerCase().includes(search.toLowerCase()) ||
                                    student.firstName?.toLowerCase().includes(search.toLowerCase()) ||
                                    student.lastName?.toLowerCase().includes(search.toLowerCase())
                                )).map(student => ([
                                    student.assigned
                                        ?   <MdOutlineCheckBox
                                                className={student.inProgress ? 'vocab__checkBox--disabled' : 'vocab__checkBox'}
                                                onClick={() => !student.inProgress && toggleAssignment(student)}
                                            />
                                        :   <MdOutlineCheckBoxOutlineBlank
                                                className='vocab__checkBox--blank'
                                                onClick={() => toggleAssignment(student)}
                                            />,
                                    `${student.firstName} ${student.lastName}`,
                                    student.email,
                                    student.assigned
                                        ? student.status === 'completed' ? 'Completed'
                                        : student.status === 'in_progress' ? 'In Progress'
                                        : 'Not Started'
                                        : '-'
                                ]))}
                            />
                        </div>
                    </>}
                </div>
            ) : (
                /* ── Vocab Quizzes tab ── */
                <div className='vocab__quizTab'>
                    {/* Quiz preview section */}
                    <div className='vocab__quizSection'>
                        <h3 className='vocab__quizSectionTitle'>Quiz Preview</h3>
                        <div className='vocab__quizSectionRow'>
                            <p className='vocab__quizSectionDesc'>Select a vocab set to view its 4 quiz variants. Click a quiz to preview the questions.</p>
                            <button className='vocab__howItWorks' onClick={() => setShowQuizWalkthrough(true)}>
                                <MdInfo /> How it works
                            </button>
                        </div>
                        <Table
                            header={['Subject', '# Words', 'Quiz 1', 'Quiz 2', 'Quiz 3', 'Quiz 4']}
                            rows={vocabSets.map(vs => ([
                                vs.subject,
                                vs.wordCount,
                                'Preview',
                                'Preview',
                                'Preview',
                                'Preview',
                            ]))}
                            onSelectCell={vocabSets.map(vs => ([
                                null,
                                null,
                                () => fetchQuizPreview(vs.id, 1, vs.subject),
                                () => fetchQuizPreview(vs.id, 2, vs.subject),
                                () => fetchQuizPreview(vs.id, 3, vs.subject),
                                () => fetchQuizPreview(vs.id, 4, vs.subject),
                            ]))}
                        />
                    </div>
                </div>
            )}

            {/* Word preview popup */}
            {(previewWords !== null || previewLoading) && (
                <Popup
                    title={`${previewSetName} — ${previewWords ? previewWords.length : '...'} words`}
                    closeButtonOnClick={() => { setPreviewWords(null); setPreviewLoading(false); }}
                    innerRef={popupRef}
                    fullScreen
                >
                    {previewLoading ? (
                        <div className='vocab__previewLoading'>
                            <img className='vocab__loader' src={Spinner} alt='Loading words...' />
                        </div>
                    ) : (
                        <div className='vocab__previewTable'>
                            <Table
                                header={['#', 'Word', 'Part of Speech', 'Definition', 'Connotation']}
                                columnTypes={['number', 'string', 'string', 'string', 'string']}
                                rows={previewWords.map((w, i) => [
                                    i + 1,
                                    w.word,
                                    w.partOfSpeech || '-',
                                    w.definition,
                                    w.connotation || '-',
                                ])}
                            />
                        </div>
                    )}
                </Popup>
            )}

            {/* Quiz preview popup */}
            {(quizPreview !== null || quizPreviewLoading) && (
                <Popup
                    title={`${quizPreviewTitle} — ${quizPreview ? quizPreview.totalQuestions : '...'} questions`}
                    closeButtonOnClick={() => { setQuizPreview(null); setQuizPreviewLoading(false); }}
                    innerRef={quizPopupRef}
                    fullScreen
                >
                    {quizPreviewLoading ? (
                        <div className='vocab__previewLoading'>
                            <img className='vocab__loader' src={Spinner} alt='Loading quiz...' />
                        </div>
                    ) : quizPreview && (
                        <div className='vocab__previewTable'>
                            <Table
                                header={['#', 'Sentence', 'Correct Answer', 'Choice A', 'Choice B', 'Choice C', 'Choice D']}
                                rows={quizPreview.questions.map((q, i) => [
                                    i + 1,
                                    q.sentence,
                                    q.correctAnswer,
                                    q.choices[0],
                                    q.choices[1],
                                    q.choices[2],
                                    q.choices[3],
                                ])}
                            />
                        </div>
                    )}
                </Popup>
            )}

            {showWalkthrough && (
                <VocabWalkthrough onClose={() => setShowWalkthrough(false)} />
            )}

            {showQuizWalkthrough && (
                <VocabQuizWalkthrough onClose={() => setShowQuizWalkthrough(false)} />
            )}
        </>
    );
}

export default Vocab;
